import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { useState, type ReactElement, type ReactNode } from 'react';
import { type ApiClient } from '../api/client';
import { apiClient as defaultApiClient } from '../api/default-client';
import { AuthProvider } from '../auth/AuthProvider';
import { type OidcClient } from '../auth/types';
import { getRuntimeConfig } from '../config/runtime-config';
import { FeatureFlagProvider } from '../features/hooks';
import { I18nProvider } from '../i18n/I18nProvider';
import { type Locale } from '../i18n/format-message';
import { SessionProvider } from '../session/SessionProvider';
import { TelemetryProvider } from '../telemetry/TelemetryProvider';
import { telemetryFromConfig } from '../telemetry/telemetry';
import { type Telemetry } from '../telemetry/types';
import { ApiClientProvider } from './ApiClientProvider';
import { createQueryClient } from './query-client';

export interface ZagruPlatformProviderProps {
  children: ReactNode;
  queryClient?: QueryClient;
  apiClient?: ApiClient;
  oidcClient?: OidcClient;
  telemetry?: Telemetry;
  locale?: Locale;
  /** Load `/me` and feature flags automatically once authenticated (default true). */
  autoLoadSession?: boolean;
  onAuthenticated?: (returnTo?: string) => void;
}

/**
 * Composes every platform provider: QueryClient → ApiClient → Telemetry → I18n → Auth → Session → FeatureFlags.
 * Instances not provided are created once from the runtime configuration.
 */
export function ZagruPlatformProvider({
  children,
  queryClient,
  apiClient,
  oidcClient,
  telemetry,
  locale,
  autoLoadSession = true,
  onAuthenticated,
}: ZagruPlatformProviderProps): ReactElement {
  const [ownQueryClient] = useState(() => queryClient ?? createQueryClient());
  const [ownTelemetry] = useState(() => telemetry ?? telemetryFromConfig(getRuntimeConfig().telemetry));
  const client = apiClient ?? defaultApiClient;
  return (
    <QueryClientProvider client={ownQueryClient}>
      <ApiClientProvider client={client}>
        <TelemetryProvider telemetry={ownTelemetry}>
          <I18nProvider locale={locale}>
            <AuthProvider client={oidcClient} onAuthenticated={onAuthenticated}>
              <SessionProvider client={client} autoLoad={autoLoadSession}>
                <FeatureFlagProvider client={client} autoLoad={autoLoadSession}>
                  {children}
                </FeatureFlagProvider>
              </SessionProvider>
            </AuthProvider>
          </I18nProvider>
        </TelemetryProvider>
      </ApiClientProvider>
    </QueryClientProvider>
  );
}
